import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import { useLabs } from '@/lib/labs-context';
import { UnitSelect } from '@/components/UnitSelect';
import { initialUnitChoice, unitChoiceToId } from '@/lib/units';
import type { LabProjectDetail, LabRemixOffer, LabTeamSummary } from '@/lib/types';

const NEW_TEAM = 'new';

export function RemixDialog({
  releaseId = null,
  slug = null,
  open,
  onOpenChange,
  onCreated,
}: {
  releaseId?: string | null;
  slug?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (project: LabProjectDetail, downloadUrl: string | null) => void;
}) {
  const labs = useLabs();
  const [offer, setOffer] = useState<LabRemixOffer | null>(null);
  const [title, setTitle] = useState('');
  const initialUnit = initialUnitChoice(labs);
  const [unitId, setUnitId] = useState(initialUnit);
  const [teams, setTeams] = useState<LabTeamSummary[]>([]);
  const [teamChoice, setTeamChoice] = useState(NEW_TEAM);
  const [teamName, setTeamName] = useState('');
  const [teamNameEdited, setTeamNameEdited] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || (!releaseId && !slug)) return;
    setOffer(null);
    setTitle('');
    setUnitId(initialUnit);
    setTeams([]);
    setTeamChoice(NEW_TEAM);
    setTeamName('');
    setTeamNameEdited(false);
    setAccepted(false);
    setError('');
    let active = true;
    const offerRequest = slug ? labsApi.remixShowcaseOffer(slug) : labsApi.remixOffer(releaseId!);
    offerRequest
      .then((result) => {
        if (!active) return;
        setOffer(result);
        setTitle(`Remix of ${result.title}`.slice(0, 120));
      })
      .catch((err) => {
        if (active) setError(getErrorMessage(err));
      });
    const loadTeams = async () => {
      const collected: LabTeamSummary[] = [];
      let nextPage = 1;
      let total = 0;
      do {
        const result = await labsApi.listTeams(labs.organization.slug, nextPage, 100);
        collected.push(...result.teams);
        total = result.total;
        nextPage += 1;
      } while (active && collected.length < total && nextPage <= 10);
      return collected;
    };
    loadTeams()
      .then((result) => {
        if (active) setTeams(result);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [initialUnit, labs.organization.slug, open, releaseId, slug]);

  useEffect(() => {
    if (!teamNameEdited) setTeamName(title);
  }, [teamNameEdited, title]);

  const create = async () => {
    if (!offer?.terms) return;
    setSaving(true);
    setError('');
    try {
      const team = teamChoice === NEW_TEAM
        ? { teamName: (teamName || title).trim() }
        : { teamId: teamChoice };
      const created = slug
        ? await labsApi.createShowcaseRemix(labs.organization.slug, {
          slug,
          termsVersion: offer.terms.version,
          title: title.trim(),
          unitId: unitChoiceToId(unitId),
          ...team,
        })
        : await labsApi.createRemix(labs.organization.slug, {
          parentReleaseId: offer.releaseId,
          termsVersion: offer.terms.version,
          title: title.trim(),
          unitId: unitChoiceToId(unitId),
          ...team,
        });
      onCreated(created.project, created.downloadUrl);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const teamReady = teamChoice !== NEW_TEAM || Boolean((teamName || title).trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <DialogHeader>
            <DialogTitle>{offer ? `Remix ${offer.title}` : 'Remix'}</DialogTitle>
          </DialogHeader>

          {!offer && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

          {offer && (
            <>
              <div className="space-y-2 text-sm">
                <ol className="space-y-1">
                  <li>
                    {offer.title} - {offer.version}
                    {offer.organization ? ` | ${offer.organization}` : ''}
                  </li>
                  {offer.ancestors.map((ancestor) => (
                    <li key={ancestor.releaseId} className="text-muted-foreground">
                      Remix of {ancestor.title} - {ancestor.version}
                      {ancestor.organization ? ` | ${ancestor.organization}` : ''}
                      {ancestor.withdrawn ? ' (taken down)' : ''}
                    </li>
                  ))}
                </ol>
                {offer.credits.length > 0 && (
                  <p className="text-muted-foreground">
                    {offer.credits.map((credit) => credit.name).join(', ')}
                  </p>
                )}
                {offer.assetDeclaration && <p>{offer.assetDeclaration}</p>}
              </div>

              {offer.terms && (
                <div className="space-y-3">
                  <h2 className="text-sm font-medium">
                    {offer.terms.kind === 'showcase' ? 'Licences' : `Internal remix terms, version ${offer.terms.version}`}
                  </h2>
                  <p className="whitespace-pre-line text-sm text-muted-foreground">{offer.terms.text}</p>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="remix-accept"
                      checked={accepted}
                      onCheckedChange={(value) => setAccepted(value === true)}
                      disabled={!offer.eligible || saving}
                    />
                    <Label htmlFor="remix-accept" className="pt-1 font-normal leading-5">
                      {offer.terms.kind === 'showcase'
                        ? 'I accept these licences for this remix'
                        : 'I accept these terms for this version'}
                    </Label>
                  </div>
                </div>
              )}

              {offer.reason && <p className="text-sm text-destructive" role="alert">{offer.reason}</p>}

              <div className="space-y-2">
                <Label htmlFor="remix-title">Title</Label>
                <Input
                  id="remix-title"
                  value={title}
                  maxLength={120}
                  disabled={!offer.eligible || saving}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="remix-team">Team</Label>
                <Select value={teamChoice} onValueChange={setTeamChoice} disabled={!offer.eligible || saving}>
                  <SelectTrigger id="remix-team">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NEW_TEAM}>New team</SelectItem>
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {teamChoice === NEW_TEAM && (
                <div className="space-y-2">
                  <Label htmlFor="remix-team-name">Team name</Label>
                  <Input
                    id="remix-team-name"
                    value={teamName}
                    maxLength={120}
                    disabled={!offer.eligible || saving}
                    onChange={(event) => {
                      setTeamNameEdited(true);
                      setTeamName(event.target.value);
                    }}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="remix-unit">Unit</Label>
                <UnitSelect id="remix-unit" value={unitId} onChange={setUnitId} disabled={!offer.eligible || saving} />
              </div>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving || !offer?.eligible || !offer.terms || !accepted || !title.trim() || !teamReady || (labs.requiresLabsUnit && !unitChoiceToId(unitId))}
            >
              {saving ? 'Creating…' : 'Create remix'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
